import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';

import Icon, { IconVariants } from 'components/foundations/Icon';
import RadioButtonsGroup from 'components/widgets/RadioButtonsGroup';
import WizardFooter from 'components/widgets/WizardFooter';
import {
    SHARED_REPORTS_ANONYMOUS_ENDPOINT,
    SHARED_REPORTS_DEFAULT_DAYS,
    SHARED_REPORTS_EXPIRATION_DAYS_CHOICES,
} from 'components/reportsWidgets/consts';
import { getFormattedDate, getSharedReportsExpirationDaysOptions } from 'components/reportsWidgets/utils';
import Tooltip from '../molecules/Tooltip';
import TextField from '../molecules/TextField';
import Dropdown from '../molecules/Dropdown';
import GeneralPopup from '../organisms/GeneralPopup';
import { SHARED_REPORTS_EVENT_PREFIX, trackMixpanelEvent } from '../../utils/general';
import css from './SharedReportsShelf.css';
import EmptyState from '../widgets/EmptyState';
import Shelf from '../widgets/Shelf';
import Button from '../atoms/Button';

import WarningMessage from '../utilities/WarningMessage';
import { validateName } from '../utilities/Sanitize';

const SharedReportsShelf = ({
    translate = () => {},
    onGenerateLinkClick = () => {},
    onDeleteReport = () => {},
    onCloseShelf = () => {},
    onGenerateList = () => {},
    onInnerShowChanged = () => {},
    sharedReportList = [],
    sharedReportId = '',
    queryMode = true,
    reportDates = {},
    show = false,
}) => {
    const [reportName, setReportName] = useState('');
    const [password, setPassword] = useState('');
    const [expirationDays, setExpirationDays] = useState(SHARED_REPORTS_DEFAULT_DAYS);
    const [dateRangeModificationOptions, setDateRangeModificationOptions] = useState([
        {
            label: translate('STATIC.PAGES.SHARED_REPORTS.SPECIFY_DATE_RANGE_FALSE'),
            id: 'specific',
            checked: true,
        },
        {
            label: translate('STATIC.PAGES.SHARED_REPORTS.SPECIFY_DATE_RANGE_TRUE'),
            id: 'all',
            checked: false,
        },
    ]);

    const [isModified, setIsModified] = useState(false);
    const [hoverIndex, setHoverIndex] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [reportDeleteInProgress, setReportDeleteInProgress] = useState(false);
    const [reportIdToDelete, setReportIdToDelete] = useState(null);
    const linkInputRef = React.useRef(null);

    useEffect(() => onGenerateList(), []);

    const toggleDateRangeModification = checkedItem => {
        setIsModified(true);
        setDateRangeModificationOptions(prevOptions =>
            prevOptions.map(x => ({
                ...x,
                checked: checkedItem.id === x.id,
            }))
        );
    };

    const handleExpirationDaysChanged = ({ name }) => {
        if (!SHARED_REPORTS_EXPIRATION_DAYS_CHOICES.includes(name)) {
            return;
        }
        setExpirationDays(name);
        setIsModified(true);
    };

    const handleReportNameChanged = newReportName => {
        setReportName(newReportName);
        setIsModified(true);
    };

    const handlePasswordChanged = newPassword => {
        setPassword(newPassword);
        setIsModified(true);
    };

    const handleGenerateButtonClicked = useCallback(() => {
        const enableFlexibleDates = dateRangeModificationOptions[1].checked;
        onGenerateLinkClick(reportName, password, expirationDays, enableFlexibleDates);
        setIsModified(false);
        trackMixpanelEvent(SHARED_REPORTS_EVENT_PREFIX, 'Report Created', {
            reportName,
            expirationDays,
        });
    }, [reportName, password, expirationDays, dateRangeModificationOptions, onGenerateLinkClick]);

    const handleDeleteReport = useCallback(reportId => {
        setReportIdToDelete(reportId);
        setShowDeleteDialog(true);
        trackMixpanelEvent(SHARED_REPORTS_EVENT_PREFIX, 'Report Deleted', {
            reportId,
        });
    }, []);

    const handleCloseShelf = useCallback(() => {
        if (isModified) {
            if (!confirm(translate('STATIC.PAGES.SHARED_REPORTS.UNSAVED_CHANGES_WARNING'))) {
                return;
            }
        }
        setReportName('');
        setPassword('');
        setExpirationDays(SHARED_REPORTS_DEFAULT_DAYS);
        setDateRangeModificationOptions([
            {
                label: translate('STATIC.PAGES.SHARED_REPORTS.SPECIFY_DATE_RANGE_FALSE'),
                id: 'specific',
                checked: true,
            },
            {
                label: translate('STATIC.PAGES.SHARED_REPORTS.SPECIFY_DATE_RANGE_TRUE'),
                id: 'all',
                checked: false,
            },
        ]);
        setIsModified(false);
        setHoverIndex(null);
        setShowDeleteDialog(false);
        setReportDeleteInProgress(false);
        setReportIdToDelete(null);

        onInnerShowChanged(false);
        onCloseShelf();
    }, [isModified, translate, onInnerShowChanged, onCloseShelf]);

    const handleCopyButtonClicked = () => {
        if (linkInputRef.current) {
            linkInputRef.current.select();
            document.execCommand('Copy');
        }
    };

    const onPopupClose = () => {
        setReportIdToDelete(null);
        setShowDeleteDialog(false);
    };

    const onAcceptReportDelete = () => {
        setReportDeleteInProgress(true);
        onDeleteReport(reportIdToDelete);
        setTimeout(() => {
            setShowDeleteDialog(false);
            setReportDeleteInProgress(false);
        }, 2000);
    };

    const validateReportName = reportName => {
        if (!isModified) {
            return '';
        }
        if (!reportName) {
            return translate('STATIC.PAGES.SHARED_REPORTS.ERRORS.REPORT_NAME_REQUIRED');
        }

        if (!validateName(reportName)) {
            return translate('STATIC.PAGES.SHARED_REPORTS.ERRORS.REPORT_NAME_INVALID');
        }
        return '';
    };

    const renderGenerateReport = (
        reportName,
        password,
        isModified,
        expirationDays,
        sharedReportId,
        link,
        reportDates
    ) => {
        const dateString = dateRangeModificationOptions[1].checked
            ? 'All dates'
            : ` ${getFormattedDate(reportDates.start_date)} - ${getFormattedDate(reportDates.end_date)}`;
        const errorMessage = validateReportName(reportName);
        return (
            <React.Fragment>
                <div className={css.headerText}>
                    <Translate id="STATIC.PAGES.SHARED_REPORTS.GENERATE_REPORT_SECTION_HEADER" />
                </div>
                <div className={css.buttonAndText}>
                    <TextField
                        inputConfig={{ maxLength: 30 }}
                        value={reportName}
                        containerClass={css.textInputLine}
                        label="STATIC.PAGES.SHARED_REPORTS.REPORT_NAME"
                        mandatory
                        onChange={handleReportNameChanged}
                        placeholder={translate('STATIC.PAGES.SHARED_REPORTS.REPORT_NAME_HINT')}
                        error={errorMessage}
                    />
                    <Button
                        type="secondary"
                        level="level1"
                        disabled={!isModified || errorMessage !== ''}
                        className={css.generateLinkButton}
                        text={translate('STATIC.PAGES.SHARED_REPORTS.GENERATE_LINK_BUTTON')}
                        onClick={handleGenerateButtonClicked}
                    />
                </div>
                <TextField
                    inputConfig={{ maxLength: 20 }}
                    value={password}
                    containerClass={css.textInputLine}
                    label="STATIC.PAGES.SHARED_REPORTS.PASSWORD"
                    placeholder={translate('STATIC.PAGES.SHARED_REPORTS.PASSWORD_HINT')}
                    onChange={handlePasswordChanged}
                />
                <div className={css.dateRangeSection}>
                    <Translate id="STATIC.PAGES.SHARED_REPORTS.DATES" />
                    <Icon name="calendar" variant={IconVariants.ACTIVE} className={css.calendarIcon} />
                    <div className={css.dateRangeString}>{dateString}</div>
                </div>
                <div className={css.dateChangeRadioButtons}>
                    <RadioButtonsGroup
                        id="dateRangeModification"
                        radioItems={dateRangeModificationOptions}
                        onChange={toggleDateRangeModification}
                    />
                </div>
                <div className={css.numOfDaysContainer}>
                    <div>
                        <Translate id="STATIC.PAGES.SHARED_REPORTS.LINK_EXPIRATION" />
                    </div>
                    <Dropdown
                        items={getSharedReportsExpirationDaysOptions()}
                        selected={{ name: expirationDays, display_name: expirationDays }}
                        containerClass={css.numOfDaysInput}
                        onSelection={handleExpirationDaysChanged}
                    />
                    <div>
                        <Translate id="STATIC.PAGES.SHARED_REPORTS.LINK_UNIT" />
                    </div>
                </div>
                <div className={css.buttonAndText}>
                    <div>Generated Link:</div>
                    <Button
                        type="link"
                        text="STATIC.PAGES.SHARED_REPORTS.COPY_BUTTON"
                        className={css.copyButton}
                        style={{ paddingBottom: '12px' }}
                        disabled={!sharedReportId || isModified}
                        onClick={handleCopyButtonClicked}
                    />
                </div>
                <textarea
                    readOnly
                    className={css.link}
                    disabled={!sharedReportId || isModified}
                    value={sharedReportId && !isModified ? link : ''}
                    ref={linkInputRef}
                />
            </React.Fragment>
        );
    };

    const renderReportsList = sharedReportsList => {
        if (!sharedReportsList || sharedReportsList.length === 0) {
            return (
                <div className={css.emptyState}>
                    <EmptyState icon="happyPage" header="STATIC.PAGES.SHARED_REPORTS.NO_EXISTING_REPORTS" />
                </div>
            );
        }
        return sharedReportsList.map(sharedReport => {
            const link = `${window.location.origin}${SHARED_REPORTS_ANONYMOUS_ENDPOINT}${sharedReport.id}`;
            const expiration = ` | ${translate('STATIC.PAGES.SHARED_REPORTS.EXPIRES')} ${getFormattedDate(
                sharedReport.expiration
            )}`;
            return (
                <div
                    className={`${css.sharedReportLine} ${
                        hoverIndex === sharedReport.id ? css.sharedReportLineBorder : ''
                    }`}
                    key={sharedReport.id}
                    onMouseEnter={() => setHoverIndex(sharedReport.id)}
                    onMouseLeave={() => setHoverIndex(null)}
                >
                    <div className={css.sharedReportLineDescription}>
                        <Tooltip interactive titleTranslationKey="STATIC.PAGES.SHARED_REPORTS.OPEN_REPORT">
                            <a
                                href={link}
                                target="_blank"
                                onClick={() => {
                                    trackMixpanelEvent(SHARED_REPORTS_EVENT_PREFIX, 'Open Report In Anonymous');
                                }}
                                rel="noreferrer"
                            >
                                {sharedReport.name}
                            </a>
                        </Tooltip>
                        {expiration}
                    </div>
                    {hoverIndex === sharedReport.id && (
                        <div
                            className={css.sharedReportLineButtons}
                            style={{ width: sharedReport.old_report ? 60 : 100 }}
                        >
                            <Tooltip interactive titleTranslationKey="STATIC.PAGES.SHARED_REPORTS.DELETE_REPORT">
                                <div
                                    onClick={() => {
                                        handleDeleteReport(sharedReport.id);
                                    }}
                                    className={css.button}
                                >
                                    <Icon name="trash" variant={IconVariants.LIGHT} />
                                </div>
                            </Tooltip>
                            {!sharedReport.old_report && (
                                <div className={css.button}>
                                    <Tooltip
                                        interactive
                                        titleTranslationKey="STATIC.PAGES.SHARED_REPORTS.OPEN_REPORT_IN_SINGULAR"
                                        onClick={() => {
                                            trackMixpanelEvent(SHARED_REPORTS_EVENT_PREFIX, 'Open Report In Singular');
                                        }}
                                    >
                                        <a href={sharedReport.url} target="_blank" rel="noreferrer">
                                            <Icon name="eye" variant={IconVariants.LIGHT} />
                                        </a>
                                    </Tooltip>
                                </div>
                            )}
                            <Tooltip interactive titleTranslationKey="STATIC.PAGES.SHARED_REPORTS.COPY_BUTTON">
                                <div
                                    onClick={() => {
                                        navigator.clipboard.writeText(link);
                                    }}
                                    className={css.button}
                                >
                                    <Icon name="link" variant={IconVariants.LIGHT} />
                                </div>
                            </Tooltip>
                        </div>
                    )}
                </div>
            );
        });
    };

    const link = `${window.location.origin}${SHARED_REPORTS_ANONYMOUS_ENDPOINT}${sharedReportId}`;

    const noExistingSharedReports = !sharedReportList || sharedReportList.length === 0;
    const showEmptyState = queryMode && noExistingSharedReports;

    const generatedLinksHeader = (
        <div className={css.headerText}>
            <Translate id="STATIC.PAGES.SHARED_REPORTS.GENERATED_LINKS" />({sharedReportList.length}):
        </div>
    );

    return (
        <React.Fragment>
            <Shelf
                open={show}
                shelfSize="medium"
                headerText="STATIC.PAGES.SHARED_REPORTS.SHELF_TITLE"
                onClose={handleCloseShelf}
            >
                <WarningMessage
                    show
                    showIcon={false}
                    showTypeIcon
                    type="info"
                    message="STATIC.PAGES.SHARED_REPORTS.NOTE"
                />
                <div
                    className={css.container}
                    onClick={e => {
                        e.stopPropagation();
                    }}
                >
                    {showEmptyState && (
                        <React.Fragment>
                            {generatedLinksHeader}
                            <div className={css.emptyState}>
                                <EmptyState icon="happyPage" header="STATIC.PAGES.SHARED_REPORTS.EMPTY_STATE" />
                            </div>
                        </React.Fragment>
                    )}
                    {!showEmptyState && !queryMode && (
                        <React.Fragment>
                            {renderGenerateReport(
                                reportName,
                                password,
                                isModified,
                                expirationDays,
                                sharedReportId,
                                link,
                                reportDates
                            )}
                            <div className={css.divider} />
                        </React.Fragment>
                    )}
                    {!showEmptyState && (
                        <div>
                            {generatedLinksHeader}
                            {renderReportsList(sharedReportList)}
                        </div>
                    )}
                </div>
                <WizardFooter buttons={[<Button text="STATIC.BUTTONS.CLOSE" onClick={handleCloseShelf} />]} />
            </Shelf>
            <GeneralPopup
                open={showDeleteDialog}
                type="delete"
                title="STATIC.PAGES.SHARED_REPORTS.DELETE_MODAL.TITLE"
                text="STATIC.PAGES.SHARED_REPORTS.DELETE_MODAL.MESSAGE"
                acceptText="STATIC.PAGES.SHARED_REPORTS.DELETE_MODAL.ACCEPT_BUTTON_TEXT"
                rejectText="STATIC.PAGES.SHARED_REPORTS.DELETE_MODAL.REJECT_BUTTON_TEXT"
                onAccept={onAcceptReportDelete}
                onReject={onPopupClose}
                showSpinner={reportDeleteInProgress}
            />
        </React.Fragment>
    );
};

SharedReportsShelf.propTypes = {
    show: PropTypes.bool,
    onGenerateLinkClick: PropTypes.func,
    onDeleteReport: PropTypes.func,
    onCloseShelf: PropTypes.func,
    onGenerateList: PropTypes.func,
    sharedReportList: PropTypes.arrayOf(PropTypes.any),
    onInnerShowChanged: PropTypes.func,
    translate: PropTypes.func,
    sharedReportId: PropTypes.string,
    queryMode: PropTypes.bool,
    reportDates: PropTypes.objectOf(PropTypes.any),
};

export default withLocalize(SharedReportsShelf);
